package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProjectProviderMode
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.site.ds.LSecretField
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun ProviderEditor(
    settings: ProviderSettingsDto,
    mode: ProjectProviderMode,
    apiKey: String,
    model: String,
    baseUrl: String,
    enabled: Boolean,
    needsModel: Boolean,
    needsBaseUrl: Boolean,
    onMode: (ProjectProviderMode) -> Unit,
    onApiKey: (String) -> Unit,
    onModel: (String) -> Unit,
    onBaseUrl: (String) -> Unit,
) {
    val optionalProjectKey = settings.provider == MtProvider.LIBRETRANSLATE && !settings.requiresKey
    val legacyClaudeModel = settings.model
        ?.trim()
        ?.takeIf { settings.provider == MtProvider.CLAUDE && it.isNotEmpty() && it !in CLAUDE_PROJECT_MODELS }
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        LSegmented(
            options = ProjectProviderMode.entries.map { modeOption ->
                LSegmentedOption(modeOption, when (modeOption) {
                    ProjectProviderMode.INHERIT -> t("app.projects.slug.settings.project_provider_editor.mode_inherit", "Follow workspace policy")
                    ProjectProviderMode.OVERRIDE -> if (settings.requiresKey) {
                        t("app.projects.slug.settings.project_provider_editor.mode_use_project_key", "Use project key")
                    } else {
                        t("app.projects.slug.settings.project_provider_editor.mode_use_project_settings", "Use project settings")
                    }
                    ProjectProviderMode.OFF ->
                        t("app.projects.slug.settings.project_provider_editor.mode_off", "Off")
                })
            },
            selected = mode,
            onSelected = onMode,
            label = tf(
                "app.projects.slug.settings.project_provider_editor.credential_mode",
                "{provider} credential mode",
                "provider" to settings.provider.displayName,
            ),
            modifier = Modifier.fillMaxWidth(),
        )
        if (mode == ProjectProviderMode.OVERRIDE) {
            if (settings.requiresKey || settings.provider == MtProvider.LIBRETRANSLATE) {
                val keyHint = settings.keyHint
                LSecretField(
                    value = apiKey,
                    onValueChange = onApiKey,
                    label = if (optionalProjectKey) {
                        tf(
                            "app.projects.slug.settings.project_provider_editor.optional_api_key_label",
                            "{provider} API key (optional)",
                            "provider" to settings.provider.displayName,
                        )
                    } else {
                        tf(
                            "app.projects.slug.settings.project_provider_editor.project_api_key_label",
                            "{provider} project API key",
                            "provider" to settings.provider.displayName,
                        )
                    },
                    placeholder = when {
                        optionalProjectKey && settings.hasUsableStoredProjectKey() -> {
                            t("app.projects.slug.settings.project_provider_editor.hint_keep_stored_optional", "Leave blank to keep the stored optional key")
                        }
                        optionalProjectKey -> t("app.projects.slug.settings.project_provider_editor.hint_optional_key", "Optional API key for this server")
                        keyHint != null -> keyHint
                        settings.hasUsableStoredProjectKey() ->
                            t("app.projects.slug.settings.project_provider_editor.hint_keep_stored", "Leave blank to keep the stored key")
                        else -> t("app.projects.slug.settings.project_provider_editor.hint_enter_key", "Enter a project key")
                    },
                    enabled = enabled,
                )
                if (optionalProjectKey) {
                    Span(
                        Modifier.color(ColorMode.current.toSitePalette().textSecondary)
                            .fontSize(TypeScale.Small)
                            .toAttrs(),
                    ) {
                        Text(t("app.projects.slug.settings.project_provider_editor.optional_leave_this_blank_when_the", "Optional. Leave this blank when the LibreTranslate server does not require an API key."))
                    }
                }
            }
            if (needsModel) {
                LSelect(
                    value = model.trim().takeIf {
                        it in CLAUDE_PROJECT_MODELS || it == legacyClaudeModel
                    }.orEmpty(),
                    options = listOf(LSelectOption("", t("app.projects.slug.settings.project_provider_editor.use_the_provider_default", "Use the provider default"))) +
                        listOfNotNull(
                            legacyClaudeModel?.let {
                                LSelectOption(
                                    it,
                                    tf(
                                        "app.projects.slug.settings.project_provider_editor.model_legacy",
                                        "{model} (legacy)",
                                        "model" to it,
                                    ),
                                )
                            },
                        ) +
                        CLAUDE_PROJECT_MODELS.map { LSelectOption(it, it) },
                    onValueChange = onModel,
                    label = t("app.projects.slug.settings.project_provider_editor.model", "Model"),
                    enabled = enabled,
                )
            }
            if (needsBaseUrl) {
                LTextField(
                    baseUrl,
                    onBaseUrl,
                    label = t("app.projects.slug.settings.project_provider_editor.base_url", "Base URL"),
                    placeholder = "https://libretranslate.com",
                    enabled = enabled,
                )
            }
        } else {
            Span(Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                Text(
                    if (mode == ProjectProviderMode.INHERIT) {
                        t("app.projects.slug.settings.project_provider_editor.this_project_follows_workspace_policy_its", "This project follows workspace policy. Its effective source can be workspace, Lingvo-funded, or none.")
                    } else {
                        t("app.projects.slug.settings.project_provider_editor.this_provider_cannot_be_used_in", "This provider cannot be used in this project.")
                    },
                )
            }
        }
    }
}
